// components/restaurant/InteractiveRestaurantCard.tsx
"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { 
  Star, 
  MapPin, 
  Shield, 
  Check, 
  Clock, 
  Users, 
  Sparkles, 
  ArrowRight,
  Tag,
  Heart,
  ExternalLink
} from "lucide-react";
import { useVendor } from "@/context/VendorContext";
interface VendorAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
}

interface VendorVerificationStatus {
  is_verified: boolean;
  verified_at: string | null;
  verified_by: number | null;
}

interface VendorDetail {
  business_type: string | null;
  price_range: string;
  cuisine_types: string[];
  features: string[];
  amenities: string[];
  tags: string[];
  logo_url: string | null;
  cover_url: string | null;
  average_rating: number;
  total_reviews: number;
}
 
interface Vendor {
  id: number;
  vendor_code: string;
  name: string;
  slug: string;
  description: string | null;
  address: VendorAddress | null;
  contact_email: string;
  contact_phone: string;
  status: 'active' | 'pending' | 'inactive' | 'suspended';
  is_verified: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  detail: VendorDetail;
  average_rating?: number;  
  owner_id: number;
  created_by: number | null;
  verification_status: VendorVerificationStatus;
  user_role: string;
}
interface InteractiveRestaurantCardProps {
  vendor: Vendor;
  onSelect?: (vendor: Vendor) => void;
}

export function InteractiveRestaurantCard({ vendor, onSelect }: InteractiveRestaurantCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { hasCartConflict } = useVendor();
  
  const fallbackImage = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop";

  const handleSelect = useCallback(() => {
    if (onSelect) {
      onSelect(vendor);
    }
  }, [vendor, onSelect]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Prevent navigation if there's a cart conflict
    if (hasCartConflict(vendor.id)) {
      e.preventDefault();
      e.stopPropagation();
      
      // Show conflict modal or message
      const shouldProceed = confirm(
        'You have items from another restaurant in your cart. ' +
        'Viewing this restaurant\'s menu will clear your current cart. Continue?'
      );
      
      if (shouldProceed) {
        handleSelect();
      }
    }
  }, [vendor.id, hasCartConflict, handleSelect]);

  const cardClasses = `
    relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 
    overflow-hidden border border-gray-200 group cursor-pointer h-full
    ${isHovered ? 'ring-2 ring-amber-500 ring-offset-2' : ''}
  `;

  return (
    <div 
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`Select ${vendor.name} restaurant`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e as any);
        }
      }}
    >
      {/* Selection Overlay */}
      {isHovered && (
        <div className="absolute inset-0 bg-gradient-to-t from-amber-500/10 to-transparent z-10 pointer-events-none" />
      )}
      
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={imageError ? fallbackImage : vendor.detail?.cover_url || fallbackImage}
          alt={`${vendor.name} - Restaurant`}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          onError={() => setImageError(true)}
          priority
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        
        {/* Top Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-lg ${
            vendor.status === "active" ? "bg-green-500" : 
            vendor.status === "pending" ? "bg-yellow-500" : 
            "bg-gray-500"
          }`}>
            {vendor.status === "active" ? "Open Now" : 
             vendor.status === "pending" ? "Opening Soon" : "Closed"}
          </div>
          
          {vendor.is_featured && (
            <div className="px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-semibold text-white shadow-lg flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Featured
            </div>
          )}
        </div>
        
        {/* Verified Badge */}
        {vendor.is_verified && (
          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-lg">
            <Shield className="w-3 h-3 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Verified</span>
          </div>
        )}
        
        {/* Price Range */}
        <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">
          {vendor.detail?.price_range || "$$"}
        </div>
      </div>
      
      {/* Content Section */}
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-gray-900 group-hover:text-amber-600 transition-colors truncate mb-2">
              {vendor.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {vendor.address?.city || "Unknown"} • {vendor.detail?.business_type || "Restaurant"}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-1 bg-amber-50 px-3 py-1.5 rounded-lg flex-shrink-0">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-bold text-gray-900">
              {(vendor.detail?.average_rating || 0).toFixed(1)}
            </span>
          </div>
        </div>
        
        {/* Cuisine Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {vendor.detail?.cuisine_types?.slice(0, 3).map((cuisine, index) => (
            <span
              key={index}
              className="px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full"
            >
              {cuisine}
            </span>
          ))}
        </div>
        
        {/* Features */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {vendor.detail?.features?.slice(0, 4).map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-xs text-gray-600">
              <Check className="w-3 h-3 text-green-500" />
              <span className="truncate">{feature}</span>
            </div>
          ))}
        </div>
        
        {/* Action Button - Only shows on hover */}
        <div className={`transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="flex gap-2">
            <Link
              href={`/vendors/${vendor.id}/menu`}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 group/btn"
              onClick={(e) => {
                if (hasCartConflict(vendor.id)) {
                  e.preventDefault();
                  handleCardClick(e);
                }
              }}
            >
              <span>View Menu</span>
              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
            
            <button
              onClick={() => {
                // Handle favorite
              }}
              className="p-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-300 hover:border-amber-500 hover:text-amber-600 transition-colors"
              aria-label="Add to favorites"
            >
              <Heart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}